import React, { useState, useEffect } from 'react';

interface SimulationControlPanelProps {
  selectedLineNumber: number;
  lineDevices: Array<{
    id: string;
    device_name: string;
    is_powered_on: boolean;
    connectivity_status: string;
    line_number: number;
  }>;
}

interface SimulationStatus {
  isRunning: boolean;
  sessionId?: string;
  sessionName?: string;
  startedAt?: string;
  metadata?: any;
}

const SimulationControlPanel: React.FC<SimulationControlPanelProps> = ({
  selectedLineNumber,
  lineDevices
}) => {
  const [simulationStatus, setSimulationStatus] = useState<SimulationStatus>({ isRunning: false });
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get line ID from line number (need to find actual line ID)
  const [lineId, setLineId] = useState<string | null>(null);

  useEffect(() => {
    fetchSimulationStatus();
    fetchLineId();
  }, [selectedLineNumber]);

  const fetchLineId = async () => {
    try {
      const response = await fetch('/api/lines');
      if (response.ok) {
        const data = await response.json();
        const line = data.data?.find((l: any) => l.lineNumber === selectedLineNumber);
        if (line) {
          setLineId(line.id);
        }
      }
    } catch (error) {
      console.error('Error fetching line ID:', error);
    }
  };

  const fetchSimulationStatus = async () => {
    try {
      const response = await fetch('/api/simulation-status');
      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        setSimulationStatus({
          isRunning: data?.session?.status === 'running',
          sessionId: data?.session?.id,
          sessionName: data?.session?.name,
          startedAt: data?.session?.started_at,
          metadata: data?.session?.metadata
        });
      }
    } catch (error) {
      console.error('Error fetching simulation status:', error);
    }
  };

  const handleStartSimulation = async () => {
    if (!lineId) {
      setError('Line ID not found');
      return;
    }

    setIsStarting(true);
    setError(null);

    try {
      const response = await fetch('/api/simulation/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `Line ${selectedLineNumber} Simulation - ${new Date().toLocaleString()}`,
          lineIds: [lineId],
          productGroupIds: []
        }),
      });

      if (response.ok) {
        await fetchSimulationStatus();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start simulation');
      }
    } catch (error) {
      setError('Error starting simulation');
      console.error('Error starting simulation:', error);
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopSimulation = async () => {
    setIsStopping(true);
    setError(null);

    try {
      const response = await fetch('/api/simulation/stop', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        await fetchSimulationStatus();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to stop simulation');
      }
    } catch (error) {
      setError('Error stopping simulation');
      console.error('Error stopping simulation:', error);
    } finally {
      setIsStopping(false);
    }
  };

  const handleClearSimulation = async () => {
    try {
      const response = await fetch('/api/simulation/clear', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        await fetchSimulationStatus();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to clear simulation');
      }
    } catch (error) {
      setError('Error clearing simulation');
      console.error('Error clearing simulation:', error);
    }
  };

  const onlineDevices = lineDevices.filter(d => d.is_powered_on && d.connectivity_status === 'online').length;
  const totalDevices = lineDevices.length;

  return (
    <div className="bg-white shadow-sm rounded-lg border border-slate-200 mb-6">
      <div className="px-6 py-4 border-b border-slate-200">
        <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
          <span className="text-2xl">⚙️</span>
          Simulation Control - Line {selectedLineNumber}
        </h3>
      </div>

      <div className="p-6">
        {/* Status Display */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${simulationStatus.isRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className="font-medium text-slate-900">
                Status: {simulationStatus.isRunning ? 'Running' : 'Stopped'}
              </span>
            </div>
            <div className="text-sm text-slate-600">
              Devices: {onlineDevices}/{totalDevices} online
            </div>
          </div>

          {simulationStatus.isRunning && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
              <div className="text-sm text-green-800">
                <div className="font-medium">Active Session: {simulationStatus.sessionName}</div>
                {simulationStatus.startedAt && (
                  <div className="text-green-600 mt-1">
                    Started: {new Date(simulationStatus.startedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}
        </div>

        {/* Control Buttons */}
        <div className="flex items-center gap-3">
          {!simulationStatus.isRunning ? (
            <button
              onClick={handleStartSimulation}
              disabled={isStarting || onlineDevices === 0}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            >
              {isStarting ? (
                <>
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Starting...
                </>
              ) : (
                <>
                  <span className="mr-2">▶️</span>
                  Start Simulation
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleStopSimulation}
              disabled={isStopping}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              {isStopping ? (
                <>
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Stopping...
                </>
              ) : (
                <>
                  <span className="mr-2">⏹️</span>
                  Stop Simulation
                </>
              )}
            </button>
          )}

          <button
            onClick={handleClearSimulation}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md transition-colors"
          >
            <span className="mr-2">🗑️</span>
            Clear Data
          </button>

          <button
            onClick={fetchSimulationStatus}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-600 bg-white hover:bg-slate-50 border border-slate-300 rounded-md transition-colors"
          >
            <span className="mr-1">🔄</span>
            Refresh
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-4 text-xs text-slate-500">
          <p>• Start Simulation: Begins MQTT data generation for all powered-on devices on this line</p>
          <p>• Stop Simulation: Ends the current simulation session</p>
          <p>• Clear Data: Removes simulation data and configurations</p>
        </div>
      </div>
    </div>
  );
};

export default SimulationControlPanel;